/**
 * Agents hub — the right-hand drawer for one agent: what it does (registry
 * description + capabilities), its REAL outputs from the latest ready run
 * (open the drafted report in the workbench, download the PPTX/XLSX), its
 * recent runs, and the actions that lead into the per-agent config page.
 *
 * Read-only: activation, schedule, settings and "Run now" live on
 * `/agents/:agentType` (AgentSkillConfig), which the drawer links to.
 */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { setMessage } from '../../../store/reducers/appSlice';
import {
  deleteCustomAgent,
  getRunArtifactUrl,
  type AgentArtifactKind,
  type AgentView,
  type RunView,
  type TypeView,
} from '../../../lib/api/client-reports/client-agents-api';
import { agentTypeMeta } from '../../../lib/agents/agent-type-meta';
import {
  agentRunTone,
  downloadableArtifacts,
  findingsChip,
  formatRunMoment,
  htmlOutputArtifact,
  runFindings,
  runHistoryStatusLabel,
  runPeriodLabel,
} from '../../client-reports/agents/agent-helpers';
import { scheduleShort } from './AgentCardTile';
import AgentOutputViewer from './AgentOutputViewer';

/** Rows shown under "Recent runs" (the list is its own scroll region). */
const MAX_RECENT_RUNS = 20;


/** "…· 38s" for a finished run; '' while still running or when unknown. */
export function runDuration(run: Pick<RunView, 'started_at' | 'finished_at'>): string {
  if (!run.started_at || !run.finished_at) return '';
  const ms =
    new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

interface Props {
  type: TypeView;
  agent: AgentView | null;
  /** This type's runs, newest first. */
  runs: RunView[];
  brandName: string;
  /** `portal.settings.manage` — shows Edit / Delete on a custom agent. */
  canManage?: boolean;
  onClose: () => void;
  /** A custom agent was deleted — the host reloads its registry. */
  onDeleted?: () => void;
  /** Edit a custom agent — the host opens the builder wizard on it. */
  onEdit?: (type: TypeView) => void;
}

const AgentDrawer = ({ type, agent, runs, brandName, canManage = false, onClose, onDeleted, onEdit }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const meta = agentTypeMeta(type, t);
  const brand = brandName || t('AgentThisBrand', 'this brand');
  const artifactLabel = (kind: AgentArtifactKind): string => {
    if (kind === 'pptx') {
      return t('AgentDrawerDownloadPptx', 'Download the PPTX deck');
    }
    if (kind === 'xlsx') {
      return t('AgentDrawerDownloadXlsx', 'Download the XLSX workbook');
    }
    return t('AgentDrawerDownloadKind', 'Download the {{kind}}', {
      kind: kind.toUpperCase(),
    });
  };
  const closeRef = useRef<HTMLButtonElement>(null);
  const [preparing, setPreparing] = useState<string | null>(null);
  /** The run whose HTML output is open in the sandboxed viewer. */
  const [viewing, setViewing] = useState<RunView | null>(null);
  /** Custom-agent delete: confirm step, then the in-flight request. */
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isCustom = !!type.custom && !!type.definition_id;

  const removeCustom = async () => {
    if (!type.definition_id || deleting) return;
    setDeleting(true);
    try {
      const result = await deleteCustomAgent(type.definition_id);
      dispatch(
        setMessage({
          message:
            result.failed > 0
              ? t(
                  'AgentDrawerDeletedPartly',
                  '{{agent}} deleted — {{failed}} brand(s) could not be switched off; AdUp has been notified.',
                  { agent: type.label, failed: result.failed }
                )
              : t('AgentDrawerDeleted', '{{agent}} deleted.', {
                  agent: type.label,
                }),
          messageType: result.failed > 0 ? 'info' : 'success',
        })
      );
      onDeleted?.();
    } catch (err: any) {
      dispatch(
        setMessage({
          message:
            err?.message ||
            t('AgentDrawerDeleteFailed', 'Failed to delete the agent.'),
          messageType: 'error',
        })
      );
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // Esc closes; focus lands on the close button so keyboard users are inside.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const latestReady = runs.find((r) => r.status === 'ready') ?? null;
  const artifacts = latestReady ? downloadableArtifacts(latestReady) : [];
  // Findings mode (Agents v3): the scorecard opens inline, the bullets show.
  const htmlOutput = latestReady ? htmlOutputArtifact(latestReady) : null;
  const findings = latestReady ? runFindings(latestReady) : [];
  const needsSetup = latestReady?.findings_status === 'needs_setup';
  const periodOrLatest = (run: RunView) =>
    runPeriodLabel(run, t) || t('AgentDrawerLatest', 'latest');
  const outputLabel = (run: RunView) =>
    type.agent_type === 'kpi_check'
      ? t('AgentDrawerOpenKpiCheck', 'Open the {{period}} KPI check →', {
          period: periodOrLatest(run),
        })
      : t('AgentDrawerOpenOutput', 'Open the {{period}} output →', {
          period: periodOrLatest(run),
        });
  // Up to the whole loaded window (20 per brand) — the list scrolls on its own.
  const recent = runs.slice(0, MAX_RECENT_RUNS);
  const configPath = `/agents/${encodeURIComponent(type.agent_type)}`;

  const download = async (run: RunView, kind: AgentArtifactKind) => {
    if (preparing) return;
    setPreparing(`${run.id}:${kind}`);
    try {
      const { url } = await getRunArtifactUrl(run.id, kind);
      // Same-tab throwaway anchor (see AgentRunHistory for the Safari note).
      const a = document.createElement('a');
      a.href = url;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err: any) {
      dispatch(
        setMessage({
          message:
            err?.message ||
            t(
              'AgentRunDownloadFailed',
              'Failed to prepare the {{kind}} download.',
              { kind: kind.toUpperCase() }
            ),
          messageType: 'error',
        })
      );
    } finally {
      setPreparing(null);
    }
  };

  const schedule = agent
    ? scheduleShort(agent.schedule?.cron, t)
    : scheduleShort(type.default_schedule?.cron, t);

  return (
    <div
      className='agentsHub__overlay'
      role='dialog'
      aria-modal='true'
      aria-label={type.label}
      onClick={onClose}
    >
      <div className='agentsHub__drawer' onClick={(e) => e.stopPropagation()}>
        <div className='agentsHub__drawer-head'>
          <div>
            <span className='agentsHub__eyebrow'>
              {meta.category} · {schedule} · {brand}
            </span>
            <h3>{type.label}</h3>
          </div>
          <button
            ref={closeRef}
            type='button'
            className='agentsHub__close'
            aria-label={t('Close', 'Close')}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {meta.description && (
          <p className='agentsHub__desc'>{meta.description}</p>
        )}
        {Array.isArray(type.capabilities) && type.capabilities.length > 0 && (
          <ul className='agentsHub__caps'>
            {type.capabilities.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        )}
        <p className='agentsHub__muted agentsHub__kpi'>
          {t(
            'AgentDrawerKpiCompares',
            'Compares results with the KPI targets set for {{brand}}.',
            { brand }
          )}{' '}
          <button
            type='button'
            className='agentsHub__linkbtn'
            onClick={() => navigate('/client-reports/targets')}
          >
            {t('AgentKpiEdit', 'Edit KPI targets →')}
          </button>
        </p>

        <h4>{t('AgentDrawerOutput', 'Output')}</h4>
        {latestReady && needsSetup ? (
          <div className='agentsHub__outcomes'>
            <p className='agentsHub__muted'>
              {t(
                'AgentDrawerNeedsKpi',
                'No KPI targets are set for {{brand}} yet, so the last run had nothing to score. Set them and the next run lands here.',
                { brand }
              )}
            </p>
            <button
              type='button'
              className='agentsHub__outcome'
              onClick={() => navigate('/client-reports/targets')}
            >
              {t('AgentKpiSet', 'Set KPI targets →')}
            </button>
          </div>
        ) : latestReady ? (
          <div className='agentsHub__outcomes'>
            {htmlOutput && (
              <button
                type='button'
                className='agentsHub__outcome'
                onClick={() => setViewing(latestReady)}
              >
                {outputLabel(latestReady)}
              </button>
            )}
            {latestReady.client_report_id && (
              <button
                type='button'
                className='agentsHub__outcome'
                onClick={() =>
                  navigate(`/client-reports/${latestReady.client_report_id}`)
                }
              >
                {t('AgentDrawerOpenReport', 'Open the {{period}} report →', {
                  period: periodOrLatest(latestReady),
                })}
              </button>
            )}
            {artifacts.map((art) => (
              <button
                key={art.kind}
                type='button'
                className='agentsHub__outcome agentsHub__outcome--secondary'
                disabled={preparing !== null}
                onClick={() => download(latestReady, art.kind)}
              >
                {preparing === `${latestReady.id}:${art.kind}`
                  ? t('AgentRunPreparing', 'Preparing…')
                  : `⬇ ${artifactLabel(art.kind)}`}
              </button>
            ))}
            {latestReady.client_report_id && (
              <button
                type='button'
                className='agentsHub__outcome agentsHub__outcome--secondary'
                onClick={() => navigate('/client-reports/reports')}
              >
                {t('AgentDrawerAllReports', 'All reports →')}
              </button>
            )}
          </div>
        ) : (
          <p className='agentsHub__muted'>
            {agent?.is_active
              ? t(
                  'AgentDrawerNoOutputActive',
                  'No output yet — the first one lands here after the next run.'
                )
              : t(
                  'AgentDrawerNoOutputOff',
                  'No output yet — switch {{agent}} on for {{brand}} and the first one lands here.',
                  { agent: type.label, brand }
                )}
          </p>
        )}

        {findings.length > 0 && (
          <>
            <h4>{t('AgentDrawerLatestFindings', 'Latest findings')}</h4>
            <ul className='agentsHub__findings' data-testid='agent-drawer-findings'>
              {findings.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </>
        )}

        <h4>{t('AgentDrawerRecentRuns', 'Recent runs')}</h4>
        {recent.length > 0 ? (
          <div className='agentsHub__runs' data-testid='agent-drawer-runs'>
            {recent.map((run) => {
              const when = formatRunMoment(
                run.finished_at || run.started_at || run.created_at,
                undefined,
                t
              );
              const dur = runDuration(run);
              // A ready findings-mode run says what it found; other runs
              // keep the plain status chip.
              const chip = findingsChip(run, t) ?? {
                label: runHistoryStatusLabel(run.status, t),
                tone: agentRunTone(run.status),
              };
              return (
                <div className='agentsHub__run' key={run.id}>
                  <span className={`agentsHub__chip agentsHub__chip--${chip.tone}`}>
                    {chip.label}
                  </span>
                  <div className='agentsHub__run-main'>
                    {(() => {
                      const text =
                        run.summary ||
                        run.error ||
                        runPeriodLabel(run, t) ||
                        t('AgentDrawerRun', 'Run');
                      return <span title={text}>{text}</span>;
                    })()}
                    <em>
                      {when}
                      {dur ? ` · ${dur}` : ''}
                    </em>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className='agentsHub__muted'>
            {t('AgentDrawerNoRuns', 'No runs yet.')}
          </p>
        )}

        <div className='agentsHub__actions'>
          <button
            type='button'
            className='agentsHub__outcome'
            onClick={() => navigate(configPath)}
          >
            {t('AgentDrawerConfigure', 'Configure Agent')}
          </button>
          {isCustom && canManage && (
            <>
              <button
                type='button'
                className='agentsHub__outcome agentsHub__outcome--secondary'
                onClick={() => onEdit?.(type)}
              >
                {t('AgentBuilderEditTitle', 'Edit agent')}
              </button>
              {confirmDelete ? (
                <span className='agentsHub__confirm-inline' role='alert'>
                  {t(
                    'AgentDrawerDeleteConfirm',
                    'Delete {{agent}} for every brand?',
                    { agent: type.label }
                  )}{' '}
                  <button type='button' className='agentsHub__linkbtn' disabled={deleting} onClick={() => void removeCustom()}>
                    {deleting
                      ? t('AgentDrawerDeleting', 'Deleting…')
                      : t('AgentDrawerYesDelete', 'Yes, delete')}
                  </button>{' '}
                  <button type='button' className='agentsHub__linkbtn' disabled={deleting} onClick={() => setConfirmDelete(false)}>
                    {t('Cancel', 'Cancel')}
                  </button>
                </span>
              ) : (
                <button
                  type='button'
                  className='agentsHub__outcome agentsHub__outcome--secondary agentsHub__outcome--danger'
                  onClick={() => setConfirmDelete(true)}
                >
                  {t('AgentDrawerDeleteAgent', 'Delete agent')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {viewing && (
        <AgentOutputViewer
          runId={viewing.id}
          title={`${type.label} — ${
            runPeriodLabel(viewing, t) || t('AgentDrawerLatestRun', 'latest run')
          }`}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

export default AgentDrawer;
